<template>
  <v-container
    fluid
  >
    <v-row
      class="docs align-start justify-center"
      wrap
    >
      <v-col
        class="ma-3"
      >
        <v-row>
          <v-col>
            <welcome-card />
          </v-col>
        </v-row>

        <v-row>
          <v-col>
            <v-card
              class="elevation-0"
              min-height="600px"
            >
              <v-card-text>
                <v-row>
                  <v-col>
                    <v-tabs
                      v-model="currentTab"
                      :value="currentTab"
                      vertical
                    >
                      <v-tab
                        v-for="(item, itemIndex) in instructions"
                        :key="itemIndex"
                        active-class="primary--text"
                        left
                        @change="currentTab = itemIndex"
                      >
                        <v-col class="pl-0 text-left subtitle-2">
                          <v-row class="px-2">
                            <v-icon
                              class="pr-2"
                              small
                            >
                              {{ item.icon }}
                            </v-icon>
                            <span>{{ item.title }}</span>
                          </v-row>
                        </v-col>
                      </v-tab>

                      <v-tab-item
                        v-for="(item, itemIndex) in instructions"
                        :key="itemIndex"
                      >
                        <v-card
                          class="elevation-0 pl-4"
                          style="border-left: 1px solid lightgray;"
                        >
                          <v-card-text v-if="item.key === 'get-started'">
                            <v-row>
                              <v-col class="py-0">
                                <p :class="`my-0 headline font-weight-bold ${titleColor}--text`">
                                  Welcome
                                </p>
                              </v-col>
                            </v-row>

                            <v-row>
                              <v-col
                                class="font-weight-medium"
                                max-width="600px"
                              >
                                <v-row>
                                  <v-col>
                                    <p class="mt-0">
                                      Visit <a
                                        href="https://beta.iadops.com"
                                        target="_blank"
                                        class="font-weight-bold"
                                      >beta.iadops.com</a> to access the Public Beta environment.
                                      Sign in with your Google-registered work email.
                                    </p>

                                    <p class="my-0">
                                      Once signed in, type your network name into the omnisearch at the top
                                      left of the header.
                                    </p>
                                  </v-col>
                                </v-row>

                                <v-row class="mt-0">
                                  <v-col class="pt-0">
                                    <video
                                      class="mt-4"
                                      width="800"
                                      controls="controls"
                                    >
                                      <source
                                        src="../../assets/videos/Go-To-Dashboard.webm"
                                        type="video/webm"
                                      >
                                    </video>
                                  </v-col>
                                </v-row>
                              </v-col>
                            </v-row>
                          </v-card-text>

                          <v-card-text v-else-if="item.key === 'saved-reports'">
                            <v-row
                              id="overview"
                              ref="overview"
                              v-intersect="onIntersect('overview')"
                            >
                              <v-col class="py-0">
                                <p :class="`my-0 headline font-weight-bold ${titleColor}--text`">
                                  Saved Reports
                                </p>

                                <p class="my-0">
                                  Review the topics below to learn more about saved reports.
                                </p>
                              </v-col>
                            </v-row>

                            <v-row>
                              <v-col>
                                <v-divider />
                              </v-col>
                            </v-row>

                            <v-row
                              id="select-report"
                              ref="select-report"
                              v-intersect="onIntersect('select-report')"
                              class="mt-9"
                            >
                              <v-col class="font-weight-medium">
                                <v-row>
                                  <v-col class="pb-0">
                                    <p class="title font-weight-bold mb-0">
                                      Select Report
                                    </p>

                                    <p class="my-0">
                                      Choose from the list of available reports.
                                    </p>
                                  </v-col>
                                </v-row>

                                <v-row class="mt-0">
                                  <v-col class="pt-0">
                                    <video
                                      class="mt-4"
                                      width="800"
                                      controls="controls"
                                    >
                                      <source
                                        src="../../assets/videos/Change-Report.webm"
                                        type="video/webm"
                                      >
                                    </video>
                                  </v-col>
                                </v-row>
                              </v-col>
                            </v-row>

                            <v-row
                              id="copy-and-edit-report"
                              ref="copy-and-edit-report"
                              v-intersect="onIntersect('copy-and-edit-report')"
                              class="mt-9"
                            >
                              <v-col class="font-weight-medium">
                                <v-row>
                                  <v-col class="pb-0">
                                    <p class="title font-weight-bold mb-0">
                                      Copy and Edit Report
                                    </p>

                                    <p class="my-0">
                                      Copy a report to create a new report from a template. Edit at will, then save your changes.
                                    </p>
                                  </v-col>
                                </v-row>

                                <v-row class="mt-0">
                                  <v-col class="pt-0">
                                    <video
                                      class="mt-4"
                                      width="800"
                                      controls="controls"
                                    >
                                      <source
                                        src="../../assets/videos/Copy-And-Edit-Report.webm"
                                        type="video/webm"
                                      >
                                    </video>
                                  </v-col>
                                </v-row>
                              </v-col>
                            </v-row>

                            <v-row
                              id="edit-report-parameter"
                              ref="edit-report-parameter"
                              v-intersect="onIntersect('edit-report-parameter')"
                              class="mt-9"
                            >
                              <v-col class="font-weight-medium">
                                <v-row>
                                  <v-col class="pb-0">
                                    <p class="title font-weight-bold mb-0">
                                      Edit Report Parameter
                                    </p>

                                    <p class="my-0">
                                      Learn how to edit a report's filter parameters.
                                    </p>
                                  </v-col>
                                </v-row>

                                <v-row class="mt-0">
                                  <v-col class="pt-0">
                                    <video
                                      class="mt-4"
                                      width="800"
                                      controls="controls"
                                    >
                                      <source
                                        src="../../assets/videos/Edit-Report-Parameter.webm"
                                        type="video/webm"
                                      >
                                    </video>
                                  </v-col>
                                </v-row>
                              </v-col>
                            </v-row>

                            <v-row
                              id="filter-report"
                              ref="filter-report"
                              v-intersect="onIntersect('filter-report')"
                              class="mt-9"
                            >
                              <v-col class="font-weight-medium">
                                <v-row>
                                  <v-col class="pb-0">
                                    <p class="title font-weight-bold mb-0">
                                      Filter Report
                                    </p>

                                    <p class="my-0">
                                      Toggle between the report's read and edit modes.
                                    </p>
                                  </v-col>
                                </v-row>

                                <v-row class="mt-0">
                                  <v-col class="pt-0">
                                    <video
                                      class="mt-4"
                                      width="800"
                                      controls="controls"
                                    >
                                      <source
                                        src="../../assets/videos/Filter-Report.webm"
                                        type="video/webm"
                                      >
                                    </video>
                                  </v-col>
                                </v-row>
                              </v-col>
                            </v-row>

                            <v-row
                              id="go-to-dashboard"
                              ref="go-to-dashboard"
                              v-intersect="onIntersect('go-to-dashboard')"
                              class="mt-9"
                            >
                              <v-col class="font-weight-medium">
                                <v-row>
                                  <v-col class="pb-0">
                                    <p class="title font-weight-bold mb-0">
                                      Go to Dashboard
                                    </p>

                                    <p class="my-0">
                                      Click your favorite network to visit its dashboard. You can also click the Dashboard navigation item in the sidebar.
                                    </p>
                                  </v-col>
                                </v-row>

                                <v-row class="mt-0">
                                  <v-col class="pt-0">
                                    <video
                                      class="mt-4"
                                      width="800"
                                      controls="controls"
                                    >
                                      <source
                                        src="../../assets/videos/Go-To-Dashboard.webm"
                                        type="video/webm"
                                      >
                                    </video>
                                  </v-col>
                                </v-row>
                              </v-col>
                            </v-row>

                            <v-row
                              id="view-report-from-dashboard"
                              ref="view-report-from-dashboard"
                              v-intersect="onIntersect('view-report-from-dashboard')"
                              class="mt-9"
                            >
                              <v-col class="font-weight-medium">
                                <v-row>
                                  <v-col class="pb-0">
                                    <p class="title font-weight-bold mb-0">
                                      View Report from Dashboard
                                    </p>

                                    <p class="my-0">
                                      Click through a DashboardCard to visit its report.
                                    </p>
                                  </v-col>
                                </v-row>

                                <v-row class="mt-0">
                                  <v-col class="pt-0">
                                    <video
                                      class="mt-4"
                                      width="800"
                                      controls="controls"
                                    >
                                      <source
                                        src="../../assets/videos/Go-To-Report-From-Dashboard.webm"
                                        type="video/webm"
                                      >
                                    </video>
                                  </v-col>
                                </v-row>
                              </v-col>
                            </v-row>

                            <v-row
                              v-if="isInternalUser"
                              id="full-demo-of-saved-reports"
                              ref="full-demo-of-saved-reports"
                              v-intersect="onIntersect('full-demo-of-saved-reports')"
                              class="mt-9"
                            >
                              <v-col class="font-weight-medium">
                                <v-row>
                                  <v-col class="pb-0">
                                    <p class="title font-weight-bold mb-0 align-center d-flex">
                                      <span>Full Demo</span>
                                      <span class="toasted mx-2 px-3 caption rounded white--text font-weight-bold">internal</span>
                                    </p>

                                    <p class="my-0">
                                      Watch the latest internal demo highlighting some of v5's most powerful features.
                                    </p>
                                  </v-col>
                                </v-row>

                                <v-row class="mt-0">
                                  <v-col class="pt-0">
                                    <iframe
                                      class="mt-6"
                                      src="https://drive.google.com/file/d/1sGYjNus579ftpJGu4COkxPWIBPse9arD/preview"
                                      width="800"
                                      height="450"
                                      allow="autoplay"
                                    />
                                  </v-col>
                                </v-row>

                                <v-row class="mt-0">
                                  <v-col class="pt-0">
                                    <p class="mt-4">
                                      This demo was recorded on 06/13/2023.
                                    </p>

                                    <p class="mt-4">
                                      This video has been embedded via <a
                                        href="https://drive.google.com/file/d/1sGYjNus579ftpJGu4COkxPWIBPse9arD/view?usp=sharing"
                                        target="_blank"
                                      >Google Drive.</a> Only OAO users have access.
                                    </p>
                                  </v-col>
                                </v-row>
                              </v-col>
                            </v-row>
                          </v-card-text>

                          <v-card-text v-else-if="item.key === 'metric-cards'">
                            <v-row
                              id="overview"
                              ref="overview"
                              v-intersect="onIntersect('overview')"
                            >
                              <v-col class="py-0">
                                <p :class="`my-0 headline font-weight-bold ${titleColor}--text`">
                                  Metric Cards
                                </p>

                                <p class="mt-0">
                                  Metric cards appear throughout the interface to summarize key insights.
                                </p>

                                <v-card max-width="830">
                                  <v-card-text>
                                    <img
                                      src="../../assets/images/metric-cards.png"
                                      alt="Metric Cards"
                                      style="max-width: 800px;"
                                    >
                                  </v-card-text>
                                </v-card>
                              </v-col>
                            </v-row>
                          </v-card-text>

                          <v-card-text v-else-if="item.key === 'bookmark-settings'">
                            <v-row
                              id="overview"
                              ref="overview"
                              v-intersect="onIntersect('overview')"
                            >
                              <v-col class="py-0">
                                <p :class="`my-0 headline font-weight-bold ${titleColor}--text`">
                                  Bookmarks
                                </p>

                                <p class="my-0">
                                  Review the topics below to learn more about bookmarks.
                                </p>
                              </v-col>
                            </v-row>

                            <v-row>
                              <v-col>
                                <v-divider />
                              </v-col>
                            </v-row>

                            <v-row
                              id="bookmark-a-report"
                              ref="bookmark-a-report"
                              v-intersect="onIntersect('bookmark-a-report')"
                              class="mt-9"
                            >
                              <v-col class="font-weight-medium">
                                <v-row>
                                  <v-col class="pb-0">
                                    <p class="title font-weight-bold mb-0">
                                      Bookmark a Report
                                    </p>

                                    <p class="my-0">
                                      Bookmark a report using the bookmark icon in the report header.
                                    </p>

                                    <v-card
                                      max-width="830"
                                      class="mt-4"
                                    >
                                      <v-card-text>
                                        <img
                                          src="../../assets/images/bookmark-report.png"
                                          alt="Bookmarks Card"
                                          style="max-width: 800px;"
                                        >
                                      </v-card-text>
                                    </v-card>
                                  </v-col>
                                </v-row>
                              </v-col>
                            </v-row>

                            <v-row
                              id="manage-bookmarks"
                              ref="manage-bookmarks"
                              v-intersect="onIntersect('manage-bookmarks')"
                              class="mt-9"
                            >
                              <v-col class="font-weight-medium">
                                <v-row>
                                  <v-col class="pb-0">
                                    <p class="title font-weight-bold mb-0">
                                      Manage Bookmarks
                                    </p>

                                    <p class="my-0">
                                      Manage your bookmarks from the Bookmarks card on the home page.
                                    </p>

                                    <v-card
                                      max-width="830"
                                      class="mt-4"
                                    >
                                      <v-card-text>
                                        <img
                                          src="../../assets/images/edit-bookmark-dashboard.png"
                                          alt="Bookmarks Card"
                                          style="max-width: 800px;"
                                        >
                                      </v-card-text>
                                    </v-card>
                                  </v-col>
                                </v-row>
                              </v-col>
                            </v-row>
                          </v-card-text>

                          <v-card-text v-else-if="item.key === 'network-settings'">
                            <v-row
                              id="overview"
                              ref="overview"
                              v-intersect="onIntersect('overview')"
                            >
                              <v-col class="py-0">
                                <p :class="`my-0 headline font-weight-bold ${titleColor}--text`">
                                  Network Settings
                                </p>
                              </v-col>
                            </v-row>

                            <v-row>
                              <v-col class="font-weight-medium">
                                <p class="mt-0">
                                  Users with multiple networks or team-delimited reports can set their primary network or team.
                                </p>

                                <p class="mt-0">
                                  Users can manage their network settings from the home page’s Networks card.
                                </p>

                                <v-card max-width="830">
                                  <v-card-text>
                                    <img
                                      src="../../assets/images/edit-network-dashboard.png"
                                      alt="Networks Card"
                                      style="max-width: 800px;"
                                    >
                                  </v-card-text>
                                </v-card>
                              </v-col>
                            </v-row>

                            <v-row>
                              <v-col class="font-weight-medium">
                                <p class="mt-0">
                                  To learn more about team-based reports, please contact <a
                                    href="mailto:tech@adops.com"
                                    target="_blank"
                                  >tech@adops.com</a>.
                                </p>
                              </v-col>
                            </v-row>
                          </v-card-text>

                          <v-card-text v-else-if="item.key === 'faq'">
                            <v-row
                              id="overview"
                              ref="overview"
                              v-intersect="onIntersect('overview')"
                            >
                              <v-col class="py-0">
                                <p :class="`my-0 headline font-weight-bold ${titleColor}--text`">
                                  FAQ
                                </p>
                              </v-col>
                            </v-row>

                            <v-row
                              id="register-your-work-email-with-google"
                              ref="register-your-work-email-with-google"
                              v-intersect="onIntersect('register-your-work-email-with-google')"
                            >
                              <v-col>
                                <p class="subtitle-2 font-weight-bold">
                                  Register Your Work Email with Google
                                </p>
                                <p class="mb-0 font-weight-medium">
                                  To allow the user to sign into iAdOps, we recommend the user register their corporate account with Google using the directions below.
                                </p>

                                <v-list dense>
                                  <v-list-item>
                                    <span class="pr-1 font-weight-bold">1.</span> Go to the <a
                                      href="https://accounts.google.com/signin"
                                      target="_blank"
                                      class="font-weight-bold px-1"
                                    >Google Account Sign-In page</a>.
                                  </v-list-item>
                                  <v-list-item>
                                    <span class="pr-1 font-weight-bold">2.</span> Click Create Account.
                                  </v-list-item>
                                  <v-list-item>
                                    <span class="pr-1 font-weight-bold">3.</span> Enter your name.
                                  </v-list-item>
                                  <v-list-item>
                                    <span class="pr-1 font-weight-bold">4.</span> Click the "Use my current email address instead" option.
                                  </v-list-item>
                                  <v-list-item>
                                    <span class="pr-1 font-weight-bold">5.</span> Enter your current email address.
                                  </v-list-item>
                                  <v-list-item>
                                    <span class="pr-1 font-weight-bold">6.</span> Click Next.
                                  </v-list-item>
                                  <v-list-item>
                                    <span class="pr-1 font-weight-bold">7.</span> Verify your email address with the code sent to your existing email.
                                  </v-list-item>
                                  <v-list-item>
                                    <span class="pr-1 font-weight-bold">8.</span> Click Verify.
                                  </v-list-item>
                                  <v-list-item>
                                    <span class="pr-1 font-weight-bold">9.</span> Return to <a
                                      href="https://beta.iadops.com"
                                      target="_blank"
                                      class="font-weight-bold px-1"
                                    >iAdOps 5</a> to sign in using your newly registered email.
                                  </v-list-item>
                                </v-list>

                                <p class="font-weight-medium">
                                  If the user encounters any issues during this process, please forward any screenshots or other information capturing the experience.
                                </p>
                              </v-col>
                            </v-row>
                          </v-card-text>

                          <v-card-text v-else-if="item.key === 'alias-mode'">
                            <v-row
                              id="overview"
                              ref="overview"
                              v-intersect="onIntersect('overview')"
                            >
                              <v-col>
                                <p :class="`my-0 headline font-weight-bold ${titleColor}--text`">
                                  Alias Mode
                                </p>
                              </v-col>
                            </v-row>

                            <v-row
                              id="activate-and-deactivate-alias-mode"
                              ref="activate-and-deactivate-alias-mode"
                              v-intersect="onIntersect('activate-and-deactivate-alias-mode')"
                            >
                              <v-col>
                                <p class="subtitle-2 font-weight-bold">
                                  Activate and Deactivate Alias Mode
                                </p>

                                <p class="font-weight-medium">
                                  Alias Mode facilitates internal users’ testing, troubleshooting, and user experience analysis by enabling them to view the application interface and functionality as an external user, also known as the aliased user.
                                </p>
                                <p class="mb-0 font-weight-medium">
                                  The instructions that follow guide internal users through the process of updating their alias mode settings.
                                </p>

                                <v-list dense>
                                  <v-list-item>
                                    <span class="pr-1 font-weight-bold">1.</span> Click your profile in the sidebar to reveal the profile menu.
                                  </v-list-item>
                                  <v-list-item>
                                    <span class="pr-1 font-weight-bold">2.</span> Choose “Settings” from the menu. Your Alias Mode settings are located here.
                                  </v-list-item>
                                  <v-list-item>
                                    <span class="pr-1 font-weight-bold">3.</span> Select a user to alias. Click the select input to choose from a list of user emails.
                                  </v-list-item>
                                  <v-list-item>
                                    <span class="pr-1 font-weight-bold">4.</span> Click the switch to the “On” position to activate, or “Off” to deactivate, alias mode.
                                  </v-list-item>
                                  <v-list-item>
                                    <span class="pr-1 font-weight-bold">5.</span> Click the “Save” button to update your settings. The application will refresh your page to render the application as the aliased user experiences it.
                                  </v-list-item>
                                </v-list>

                                <p class="font-weight-medium">
                                  All changes made while in alias mode are made to the aliased user’s account.
                                  This includes any changes to the aliased user’s settings and reports. For example, if an internal
                                  creates a Dashboard Card while in alias mode, the external user will see that card in their view.
                                </p>

                                <p class="font-weight-medium">
                                  When you deactivate alias mode, any changes made to the external user's account will persist.
                                  In the example above, the new Dashboard Card would remain in the external user's view.
                                </p>

                                <p class="font-weight-medium">
                                  Similarly, if you create a report while in alias mode, the aliased user will see that report in their
                                  reports list. By default, externals are the owner of any report created by internals in alias mode.
                                  However, internals can choose to save the report to their own account when creating the report.
                                  To do this, an internal can simply select their own email from the Owner input, located in the
                                  ReportBuilder's Permissions tab. This will prevent the external from seeing the report until internals
                                  are ready to share it with them. Sharing can occur by changing the report's owner or using the Share feature,
                                  also in the ReportBuilder's Permissions tab.
                                </p>
                              </v-col>
                            </v-row>
                          </v-card-text>

                          <v-card-text v-else>
                            <v-row>
                              <v-col class="font-weight-medium">
                                <p class="mt-0">
                                  Error
                                </p>
                              </v-col>
                            </v-row>
                          </v-card-text>
                        </v-card>
                      </v-tab-item>
                    </v-tabs>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-row>
          <v-col>
            <v-divider />
          </v-col>
        </v-row>

        <v-row>
          <v-col>
            <span class="headline">Legacy Documentation</span>
          </v-col>
        </v-row>

        <v-row class="align-start">
          <doc-card
            title="Features"
            type="feature"
            class="ma-3"
          />

          <doc-card
            title="Saved Filters"
            type="filter"
            class="ma-3"
          />

          <doc-card
            title="Release Notes"
            type="release"
            class="ma-3"
          />

          <doc-card
            title="Roadmaps"
            type="roadmap"
            class="ma-3"
          />

          <doc-card
            title="Other"
            type="legal"
            class="ma-3"
          />
        </v-row>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'

const { mapGetters: mapAuthGetters } = createNamespacedHelpers('auth')
const { mapGetters: mapLayoutGetters } = createNamespacedHelpers('layout')

export default {
  name: 'Docs',
  components: {
    DocCard: () => import('../components/DocCard.vue'),
    WelcomeCard: () => import('../components/WelcomeCard.vue'),
  },
  props: {
    query: {
      type: Object,
      default: () => ({}),
    },
    associationId: {
      type: String,
      default: null,
    },
    baseReportName: {
      type: String,
      default: 'docs',
    },
    subId: {
      type: String,
      default: 'all-secondary',
    },
  },
  data() {
    return {
      instructions: [
        {
          active: false,
          icon: 'flag',
          key: 'get-started',
          title: 'Get Started',
        },
        {
          active: false,
          icon: 'save_alt',
          key: 'saved-reports',
          title: 'Saved Reports',
        },
        {
          active: false,
          icon: 'filter_list',
          key: 'metric-cards',
          title: 'Metric Cards',
        },
        {
          active: false,
          icon: 'bookmark',
          key: 'bookmark-settings',
          title: 'Bookmarks',
        },
        {
          active: false,
          icon: 'dashboard',
          key: 'network-settings',
          title: 'Networks',
        },
        {
          active: false,
          icon: 'help',
          key: 'faq',
          title: 'FAQ',
        },
      ],
      tab: null,
    }
  },
  computed: {
    ...mapAuthGetters([
      'isInternalUser',
    ]),
    ...mapLayoutGetters([
      'textColor',
      'titleColor',
    ]),
    currentTab: {
      get() {
        const key = this.$route.query.section || this.instructions[0].key
        const foundIndex = this.instructions.findIndex((instruction) => instruction.key === key)
        return foundIndex === -1 ? 0 : foundIndex
      },
      set(newIndex) {
        const currentIndex = this.currentTab
        const currentInstruction = this.instructions[currentIndex]
        const newInstruction = this.instructions[newIndex]

        if (newInstruction && currentInstruction.key !== newInstruction.key) {
          this.$router.push({
            query: {
              ...this.$route.query,
              section: newInstruction.key,
              topic: 'overview',
            },
          })
        }
      },
    },
    currentTopic: {
      get() {
        return this.$route.query.topic || 'overview'
      },
      set(newTopic) {
        if (this.currentTopic !== newTopic) {
          this.$router.push({
            query: {
              ...this.$route.query,
              topic: newTopic,
            },
          })
        }
      },
    },
  },
  created() {
    if (this.$route.query.topic) {
      this.$nextTick(() => {
        this.scrollToTopic()
      })
    }
  },
  methods: {
    onIntersect(topic) {
      const threshold = 0.75

      return {
        handler: (entries) => {
          const isVisibilityThresholdMet = entries[0].intersectionRatio >= threshold

          if (isVisibilityThresholdMet) {
            this.currentTopic = topic
          }
        },
        options: {
          threshold: [0, threshold, 1.0],
        },
      }
    },
    scrollToTopic() {
      const topic = this.currentTopic

      if (topic) {
        this.$vuetify.goTo(`#${topic}`, {
          duration: 500,
          offset: 0,
        })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~src/styles/variables";

.docs {
  .card-title {
    font-size: medium;
    font-weight: bold;
    letter-spacing: 0.69px;
    line-height: 29px;
  }

  .welcome-card {
    .card-title {
       color: $color-monochrome-brightblue;
      justify-content: center;
    }
  }
}
</style>
